import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Flame, Route as RouteIcon, UserPlus, Check, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtDistance, todayStr } from "../lib/format";

export default function PublicProfile() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api
      .get(`/social/profile/${code}?today=${todayStr()}`)
      .then(({ data }) => setProfile(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Profil nicht gefunden"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [code]);

  const sendRequest = async () => {
    try {
      const { data } = await api.post("/social/request-by-code", { code });
      toast.success(data.status === "accepted" ? "Ihr seid jetzt Freunde!" : "Anfrage gesendet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  if (loading) return <div className="max-w-lg mx-auto px-5 pt-safe text-neutral-500 text-sm">Laden...</div>;

  if (error)
    return (
      <div className="max-w-lg mx-auto px-5 pt-safe">
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-neutral-400 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Zurück</button>
        <div className="bg-[#121212] border border-[#FF3366]/40 rounded-2xl p-6 text-center" data-testid="profile-error">
          <p className="text-[#FF3366] font-bold">{error}</p>
        </div>
      </div>
    );

  const rel = profile.relation;

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe" data-testid="public-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-neutral-400 text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Zurück</button>

      <div className="flex flex-col items-center text-center mb-8">
        <div className="w-24 h-24 rounded-full bg-[#CCFF00] flex items-center justify-center font-display font-black text-4xl text-black mb-4">
          {profile.name?.[0]?.toUpperCase()}
        </div>
        <h1 className="font-display font-black text-3xl tracking-tighter" data-testid="profile-name">{profile.name}</h1>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-4 text-center">
          <Flame className="w-5 h-5 text-[#CCFF00] mx-auto mb-1" />
          <div className="font-display font-black text-2xl tracking-tighter">{profile.streak}</div>
          <div className="data-label">Streak</div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-4 text-center">
          <RouteIcon className="w-5 h-5 text-[#CCFF00] mx-auto mb-1" />
          <div className="font-display font-black text-2xl tracking-tighter">{fmtDistance(profile.total_distance_m)}</div>
          <div className="data-label">km gesamt</div>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-4 text-center">
          <div className="font-display font-black text-2xl tracking-tighter mt-6">{profile.total_runs}</div>
          <div className="data-label">Läufe</div>
        </div>
      </div>

      {rel === "self" && <p className="text-center text-neutral-500 text-sm">Das ist dein eigenes Profil.</p>}
      {rel === "friends" && (
        <div className="w-full bg-[#121212] border border-[#CCFF00]/30 text-[#CCFF00] font-bold rounded-full py-4 flex items-center justify-center gap-2" data-testid="already-friends">
          <Check className="w-5 h-5" /> Ihr seid Freunde
        </div>
      )}
      {rel === "outgoing" && (
        <div className="w-full bg-[#121212] border border-white/10 text-neutral-400 font-bold rounded-full py-4 flex items-center justify-center gap-2" data-testid="request-pending">
          <Clock className="w-5 h-5" /> Anfrage gesendet
        </div>
      )}
      {(rel === "none" || rel === "incoming") && (
        <button onClick={sendRequest} data-testid="send-request-btn" className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <UserPlus className="w-5 h-5" /> {rel === "incoming" ? "Anfrage annehmen" : "Freund hinzufügen"}
        </button>
      )}
    </div>
  );
}
